import { useState, useCallback } from 'react';

import type { OnboardingData, UserPersona } from '../components/Onboarding/onboardingTypes';
import { supabase } from '../lib/supabase';
import { logEvent } from '../services/loggingService';

export const useOnboarding = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [totalSteps] = useState(5);
  const [data, setData] = useState<Partial<OnboardingData>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userPersona, setUserPersona] = useState<UserPersona | null>(null);

  // Update onboarding data
  const updateData = useCallback((updates: Partial<OnboardingData>) => {
    setData(prev => ({ ...prev, ...updates }));
    setError(null);
    
    // Auto-detect user persona based on data
    const updatedData = { ...data, ...updates };
    if (updatedData.experienceLevel) {
      if (updatedData.experienceLevel === 'Senior' || updatedData.experienceLevel === 'Executive') {
        setUserPersona('tech_senior');
      } else if (updatedData.experienceLevel === 'Junior') {
        setUserPersona('tech_junior');
      } else {
        setUserPersona('professional');
      }
    }
  }, [data]);

  // Navigate to next step
  const nextStep = useCallback(() => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
      setError(null);
    }
  }, [currentStep, totalSteps]);

  // Navigate to previous step
  const prevStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      setError(null);
    }
  }, [currentStep]);

  // Get current question type based on step and persona
  const getCurrentQuestionType = useCallback((): string => {
    switch (currentStep) {
      case 1:
        return 'welcome';
      case 2:
        return 'industry';
      case 3:
        return 'experience';
      case 4:
        return 'goals';
      case 5:
        return 'discovery';
      case 6:
        return 'completion';
      default:
        return 'welcome';
    }
  }, [currentStep, data]);

  // Calculate progress percentage
  const calculateProgress = useCallback((): number => {
    return Math.round((currentStep / totalSteps) * 100);
  }, [currentStep, totalSteps]);

  // Validate current step
  const isCurrentStepValid = useCallback((): boolean => {
    switch (currentStep) {
      case 1:
        return true; // Welcome step is always valid
      case 2:
        return !!data.industry;
      case 3:
        return !!data.experienceLevel;
      case 4:
        return true; // Career goals are optional
      case 5:
        return true; // How heard about us is optional
      default:
        return false;
    }
  }, [currentStep, data]);

  // Submit onboarding data
  const submitOnboarding = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // CRITICAL FIX: Set localStorage flag IMMEDIATELY for all users
      localStorage.setItem('onboarding_permanently_disabled', 'true');
      console.log('Onboarding completion flag set in localStorage');

      // Try to get Supabase OAuth user session
      const { data: { session } } = await supabase.auth.getSession();
      
      // Also check for phone auth session
      const phoneAuthSession = localStorage.getItem('phone_auth_session');
      let userId = session?.user?.id;
      
      if (!userId && phoneAuthSession) {
        try {
          const phoneSession = JSON.parse(phoneAuthSession);
          userId = phoneSession.user_id;
          console.log('Using phone auth user ID for onboarding:', userId);
        } catch (e) {
          console.error('Error parsing phone session:', e);
        }
      }

      if (!userId) {
        console.warn('No user ID found, onboarding data saved to localStorage only');
        // Even without user ID, localStorage flag is set, so onboarding won't reappear
        setIsLoading(false);
        return; // Don't throw error, allow completion
      }

      console.log('≡ƒô¥ Updating user record with onboarding completion...');
      
      // Update user record with onboarding completion
      const { error: updateError } = await supabase
        .from('users')
        .update({
          industry: data.industry,
          experience_level: data.experienceLevel,
          career_goals: data.careerGoals || [],
          preferred_job_titles: data.preferredJobTitles || [],
          how_heard_about_us: data.howHeardAboutUs || 'Not specified',
          onboarding_complete: true,
          onboarding_completed_at: new Date().toISOString(),
          app_tour_complete: false,
          tour_completed_at: null,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);

      if (updateError) {
        console.error('Failed to save to users table:', updateError);
        // Don't throw - localStorage flag is set, continue
      } else {
        console.log('User record updated successfully');
      }
      
      // Also update user_preferences table to ensure consistency
      const { error: preferencesError } = await supabase
        .from('user_preferences')
        .upsert({
          user_id: userId,
          onboarding_complete: true,
          onboarding_completed_at: new Date().toISOString(),
          app_tour_complete: false,
          tour_completed_at: null,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (preferencesError) {
        console.warn('ΓÜá∩╕Å Failed to update user_preferences (non-critical):', preferencesError);
      } else {
        console.log('Γ£à User preferences updated successfully');
      }

      await logEvent({
        level: 'info',
        action: 'onboarding_completed',
        details: {
          persona: userPersona,
          industry: data.industry,
          experienceLevel: data.experienceLevel,
          careerGoalsCount: data.careerGoals?.length || 0,
          howHeardAboutUs: data.howHeardAboutUs || 'Not specified'
        }
      });

    } catch (err) {
      console.error('Error submitting onboarding:', err);
      // Don't set error or throw - localStorage flag is set, onboarding is complete
      console.warn('Onboarding completion proceeded despite error (localStorage flag is set)');
    } finally {
      setIsLoading(false);
    }
  }, [data, userPersona]);

  return {
    currentStep,
    totalSteps,
    data,
    isLoading,
    error,
    userPersona,
    updateData,
    nextStep,
    prevStep,
    submitOnboarding,
    getCurrentQuestionType,
    calculateProgress,
    isCurrentStepValid
  };
};
